import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
} from "reactstrap";

import CreateMembers from "./Components/Create";
import ListSearch from "../../Components/Common/ListSearch";
import { use } from "echarts";
import { fetchMembersList } from "../../slices/member/action";
import MemberList from "./Components/MemberList";
import Pagination from "../../Components/Common/Pagination";
import Loader from "../../Components/Common/Loader";
import { getInitials } from "../../helpers/general";

const Members = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [apiSuccess, setApiSuccess] = useState(false);
  const [modalData, setModalData] = useState({});
  const [modal, setModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [pageSize, setPageSize] = useState(10);

  // Fetch data from the API
  const fetchList = useCallback(async (data) => {
    setIsLoading(true);
    try {
      const response = await fetchMembersList({ ...data });
      const { items = [], pageInfo = {} } = response || {};
      const { currentPage = 1, totalPages = 1, totalItems = 0 } = pageInfo;
      setList(items);
      setCurrentPage(currentPage);
      setTotalPages(totalPages);
      setTotalItems(totalItems);
    } catch (error) {
      console.error("Failed to fetch member list:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchList({
        pagination: { page: currentPage, limit: pageSize },
        filter: { search },
      });
    }, 300); // Debounce time of 300 milliseconds
    return () => clearTimeout(delay);
  }, [search, currentPage, fetchList, apiSuccess]);

  const handleModal = () => {
    setModal(!modal);
    if (!modal) {
      setModalData({});
    }
  };

  const [selectedAction, setSelectedAction] = useState(null);

  const toggleDropdown = (itemId) => {
    setDropdownOpen((prevState) => ({
      ...prevState,
      [itemId]: !prevState[itemId],
    }));
  };

  const handleConfirmDelete = () => {
    if (selectedAction) {
      console.log(`Confirmed delete for: ${selectedAction}`);
      // Add your delete logic here
    }
    setModal(false);
    setSelectedAction(null);
  };

  const handleActionClick = (action, data) => {
    if (action === "edit") {
      setModalData(data);
      setModal(true);
    }
  };

  return (
    <div className="page-content px-4 pb-0">
      <Row>
        <Col md={12} className="m-auto">
          <div
            style={{ backdropFilter: "blur(4px)" }}
            className="position-sticky top-0 z-1 d-flex justify-content-between align-items-center py-4"
          >
            <h4 className="mb-0">Manage Members</h4>
            <CreateMembers
              modal={modal}
              handleModal={handleModal}
              modalData={modalData}
              setApiSuccess={setApiSuccess}
            />
          </div>
          {/* <div className="mb-4">
            <ListSearch />
          </div> */}
          <div className="position-relative d-flex flex-column mt-3">
            <table className="table table-nowrap table-centered align-top mb-0">
              <thead className="bg-light text-mute fs-14">
                <tr>
                  <th></th>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="fs-13">
                {list?.map((member, index) => (
                  <tr key={member.id}>
                    <td className="text-center">{index + 1}</td>
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <span
                          className="member rounded-circle bg-white position-relative"
                          style={{ width: 26, height: 26 }}
                        >
                          <span className="d-flex justify-content-center align-items-center rounded-circle bg-dark-subtle a few seconds text-dark w-100 h-100 fs-10">
                            {getInitials(member?.name)}
                          </span>
                        </span>
                        <div>
                          <p className="mb-0">{member?.name}</p>
                          <span className="fs-10 text-muted d-block">
                            Created at: {member?.createdAt}
                          </span>
                          <span className="fs-10 text-muted d-block">
                            Updated at: {member?.updatedAt}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p className="mb-0 d-flex align-items-center gap-1">
                        {/* Conditionally render icon based on the 'copied' state */}
                        {/* <i
  className={`fs-16 cursor-pointer ${
    copied ? "bx bxs-copy text-dark" : "bx bx-copy text-muted"
  }`}
  onClick={() => handleCopyEmail(member.email)} // Handle copy on click
></i> */}
                        {member?.email}
                        {member?.isVerified ? (
                          <Badge
                            color="success"
                            className="rounded-pill d-flex align-items-center"
                          >
                            <i className="bx bx-check-double fs-16"></i>
                            Verified
                          </Badge>
                        ) : (
                          <Badge
                            color="warning"
                            className="rounded-pill d-flex align-items-center"
                          >
                            <i className="bx bx-check-double fs-16"></i>
                            Pending
                          </Badge>
                        )}
                      </p>
                      <p className="mb-0">{member.phone}</p>
                    </td>
                    <td>
                      <p className="mb-0">
                        <strong>{member.role}</strong>
                      </p>
                    </td>
                    <td>
                      <p className="mb-0">
                        <Badge
                          color={
                            member?.isActive
                              ? "success-subtle"
                              : "warning-subtle"
                          }
                          className={`rounded-pill border  ${
                            member?.isActive
                              ? "border-success text-success"
                              : "border-warning text-warning"
                          }`}
                        >
                          {member?.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </p>
                    </td>
                    <td className="text-center">
                      <Dropdown
                        isOpen={dropdownOpen[index]}
                        toggle={() => toggleDropdown(index)}
                      >
                        <DropdownToggle className="bg-transparent border-0 p-0 shadow-none text-dark ms-auto">
                          <i className="bx bx-dots-horizontal-rounded"></i>
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem
                            className="fs-13 d-flex align-items-center gap-1 text-danger"
                            onClick={() => handleActionClick("edit", member)}
                          >
                            <i className="bx bx-pencil"></i>
                            Edit
                          </DropdownItem>
                          <DropdownItem
                            className="fs-13 d-flex align-items-center gap-1"
                            // onClick={() => handleActionClick("delete")}
                          >
                            <i className="bx bx-trash"></i>
                            Delete
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isLoading ? <Loader /> : null}
          </div>
          <Pagination
            totalPages={totalPages}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            itemsPerPage={pageSize}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Members;
